// =============================================================================
// SHANIZ GAMING ADMIN - RBAC PERMISSION HOOK
// =============================================================================

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rolesService, AdminRole } from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Known permission keys used throughout the admin app.
 * Backend should expose at least these keys via GET /admin/permissions.
 */
export const PERMISSION_KEYS = [
  'users.view',
  'users.edit',
  'users.ban',
  'users.force_logout',
  'kyc.view',
  'kyc.approve',
  'kyc.reject',
  'payouts.view',
  'payouts.approve',
  'payouts.retry',
  'payouts.cancel',
  'reconciliation.run',
  'reconciliation.resolve',
  'fraud.view',
  'fraud.flag',
  'fraud.investigate',
  'tickets.view',
  'tickets.reply',
  'tickets.escalate',
  'tickets.close',
  'tickets.assign',
  'disputes.view',
  'disputes.resolve',
  'audit.view',
  'audit.export',
  'reports.view',
  'reports.export',
  'reports.schedule',
  'feature_flags.view',
  'feature_flags.edit',
  'roles.view',
  'roles.edit',
  'games.view',
  'games.force_end',
  'games.flag',
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

// Sensible default matrix used as fallback while the server is unreachable so
// the UI does not become completely read-only. Backend remains the source of
// truth — it should always enforce permissions.
const DEFAULT_MATRIX: Record<AdminRole, Partial<Record<PermissionKey, boolean>>> = {
  // SUPER_ADMIN — full platform control.
  SUPER_ADMIN: Object.fromEntries(PERMISSION_KEYS.map((k) => [k, true])) as Record<PermissionKey, boolean>,
  // FINANCE_ADMIN — payouts, reconciliation, transactions, financial reporting.
  FINANCE_ADMIN: {
    'users.view': true,
    'payouts.view': true,
    'payouts.approve': true,
    'payouts.retry': true,
    'payouts.cancel': true,
    'reconciliation.run': true,
    'reconciliation.resolve': true,
    'audit.view': true,
    'audit.export': true,
    'reports.view': true,
    'reports.export': true,
    'reports.schedule': true,
  },
  // GAME_ADMIN — games, tournaments, leaderboard, anti-cheat, feature flags.
  GAME_ADMIN: {
    'users.view': true,
    'games.view': true,
    'games.force_end': true,
    'games.flag': true,
    'reports.view': true,
    'feature_flags.view': true,
    'feature_flags.edit': true,
    'audit.view': true,
  },
  // RISK_ANALYST — fraud & risk, KYC, disputes, player reports, anti-cheat.
  RISK_ANALYST: {
    'users.view': true,
    'users.ban': true,
    'kyc.view': true,
    'kyc.approve': true,
    'kyc.reject': true,
    'fraud.view': true,
    'fraud.flag': true,
    'fraud.investigate': true,
    'disputes.view': true,
    'disputes.resolve': true,
    'games.view': true,
    'games.flag': true,
    'reports.view': true,
    'audit.view': true,
  },
  // SUPPORT_AGENT — support tickets, KYC view, player reports.
  SUPPORT_AGENT: {
    'users.view': true,
    'kyc.view': true,
    'tickets.view': true,
    'tickets.reply': true,
    'tickets.escalate': true,
    'tickets.close': true,
    'tickets.assign': true,
    'disputes.view': true,
    'reports.view': true,
  },
};

// Map legacy / free-form role strings onto the five canonical roles. Unknown
// values fall back to the least-privileged role so a mis-typed role never
// silently grants elevated access.
function normalizeRole(role: string | undefined): AdminRole {
  if (!role) return 'SUPPORT_AGENT';
  const n = role.toUpperCase().replace(/[\s-]/g, '_');
  if ((['SUPER_ADMIN', 'FINANCE_ADMIN', 'GAME_ADMIN', 'RISK_ANALYST', 'SUPPORT_AGENT'] as string[]).includes(n)) {
    return n as AdminRole;
  }
  const legacy: Record<string, AdminRole> = {
    ADMIN: 'SUPER_ADMIN',
    SUPERADMIN: 'SUPER_ADMIN',
    OWNER: 'SUPER_ADMIN',
    FINANCE: 'FINANCE_ADMIN',
    FINANCEADMIN: 'FINANCE_ADMIN',
    GAME: 'GAME_ADMIN',
    GAMES: 'GAME_ADMIN',
    GAMEADMIN: 'GAME_ADMIN',
    MODERATOR: 'RISK_ANALYST',
    FRAUD: 'RISK_ANALYST',
    FRAUDANALYST: 'RISK_ANALYST',
    RISK: 'RISK_ANALYST',
    SUPPORT: 'SUPPORT_AGENT',
    SUPPORTAGENT: 'SUPPORT_AGENT',
    AGENT: 'SUPPORT_AGENT',
    READ_ONLY: 'SUPPORT_AGENT',
    READONLY: 'SUPPORT_AGENT',
  };
  return legacy[n] ?? legacy[n.replace(/_/g, '')] ?? 'SUPPORT_AGENT';
}

export function useRolePermissions() {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);

  const query = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: rolesService.getAll,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  const matrix = useMemo(() => {
    if (query.data && query.data.length) {
      const m: Record<string, Record<string, boolean>> = {};
      for (const r of query.data) m[r.role] = r.permissions;
      return m;
    }
    return DEFAULT_MATRIX as unknown as Record<string, Record<string, boolean>>;
  }, [query.data]);

  return { role, matrix, isLoading: query.isLoading };
}

export function usePermission(key: PermissionKey): { allowed: boolean; reason: string | null } {
  const { role, matrix } = useRolePermissions();
  const allowed = Boolean(matrix[role]?.[key]);
  return {
    allowed,
    reason: allowed ? null : `Role ${role} lacks permission "${key}"`,
  };
}

/**
 * Role-based visibility check (FR-ADM-01). SUPER_ADMIN sees everything.
 * `allowed === undefined` (no restriction declared) means every role may access.
 * `allowed === []` means SUPER_ADMIN only.
 */
export function roleAllowed(role: AdminRole, allowed?: AdminRole[]): boolean {
  if (role === 'SUPER_ADMIN') return true;
  if (allowed === undefined) return true;
  return allowed.includes(role);
}

/** Current normalized admin role for the signed-in user. */
export function useAdminRole(): AdminRole {
  return useRolePermissions().role;
}
